/**
 * Product Features Section - Custom Element
 * 
 * Features:
 * - Splide slider with conditional drag behavior
 * - Modal for detailed feature view
 * - Video accessibility with prefers-reduced-motion support
 * - Focus trapping and keyboard navigation
 */

(function () {
  'use strict';

  class ProductFeaturesSection extends HTMLElement {
    constructor() {
      super();

      // Core elements
      this.sliderElement = this.querySelector('.js-product-features-slider');
      if (!this.sliderElement) return;

      this.sectionId = this.sliderElement.id;
      this.extractedSectionId = this.id.replace('ProductFeatures-', '');
      this.blockCount = parseInt(this.dataset.blockCount || '0');
      this.stickyPdpForm = document.querySelector('[data-sticky-pdp-form]');

      // Splide instances
      this.mainSplide = null;
      this.modalTextSlider = null;
      this.modalImageSlider = null;

      // Modal elements
      this.modal = document.getElementById('ProductFeaturesModal-' + this.extractedSectionId);

      // Focus management
      this.lastFocusedElement = null;
      this.focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

      // Event handlers (store references for cleanup)
      this.handleResize = this.debounce(this.updateSlider.bind(this), 250);
      this.handleKeydown = this.handleEscapeKey.bind(this);
      this.trapFocusHandler = this.trapFocus.bind(this);
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleMotionChange = this.handleReducedMotionChange.bind(this);

      // Initialize
      this.init();
    }

    init() {
      // Wait for Splide to be available
      if (typeof Splide === 'undefined') {
        setTimeout(() => this.init(), 50);
        return;
      }

      this.initSlider();
      this.initVideoControls();
      if (this.modal) {
        this.setupModal();
      }

      // Add resize listener
      window.addEventListener('resize', this.handleResize);

      // Listen for reduced motion changes
      this.prefersReducedMotion.addEventListener('change', this.handleMotionChange);
    }

    /**
     * Determine if slider should be enabled
     */
    shouldEnableSlider() {
      const isMobile = window.innerWidth < 1024;
      const hasEnoughBlocks = this.blockCount >= 5;
      return isMobile || hasEnoughBlocks;
    }

    /**
     * Initialize main slider
     */
    initSlider() {
      this.mainSplide = window.SplideUtils.initSplideWithControls(
        this.sliderElement,
        this,
        {
          type: 'carousel',
          rewind: true,
          perPage: 4,
          perMove: 1,
          gap: '1.5rem',
          padding: { left: 0, right: 0 },
          arrows: false,
          pagination: false,
          trimSpace: 'move',
          drag: this.shouldEnableSlider(),
          breakpoints: {
            1024: {
              perPage: 3,
              trimSpace: 'move',
            },
            767: {
              perPage: 2,
              gap: '1rem',
              trimSpace: 'move',
              padding: { right: '22%' },
              drag: true,
            },
            576: {
              perPage: 1,
              gap: '1rem',
              padding: { right: '22%' },
              trimSpace: 'move',
              drag: true,
            }
          },
          i18n: {
            prev: 'Previous slide',
            next: 'Next slide',
            first: 'Go to first slide',
            last: 'Go to last slide',
            slideX: 'Go to slide %s',
            pageX: 'Go to page %s',
            play: 'Start autoplay',
            pause: 'Pause autoplay',
          },
        },
        {
          customArrows: true,
          scrollbar: true,
          tabindex: true
        }
      );

      this.updateSlider();
    }

    /**
     * Update slider state based on conditions
     */
    updateSlider() {
      if (!this.mainSplide) return;

      const shouldEnable = this.shouldEnableSlider();

      if (shouldEnable) {
        this.classList.add('slider-active');
        this.mainSplide.options.drag = true;
        this.mainSplide.refresh();
      } else {
        this.classList.remove('slider-active');
        this.mainSplide.options.drag = false;
        this.mainSplide.go(0);
        this.mainSplide.refresh();
      }
    }


    /**
     * Initialize video accessibility controls
     */
    initVideoControls() {
      const videos = this.querySelectorAll('[data-decorative-video]');

      videos.forEach((video) => {
        // Pause video if reduced motion is preferred
        if (this.prefersReducedMotion.matches) {
          video.pause();
        }

        // Click on video to toggle play/pause (only effective when reduced motion is on)
        video.addEventListener('click', () => {
          if (!this.prefersReducedMotion.matches) return;

          if (video.paused) {
            video.play();
          } else {
            video.pause();
          }
        });
      });
    }

    /**
     * Handle reduced motion preference changes
     */
    handleReducedMotionChange(e) {
      const videos = this.querySelectorAll('[data-decorative-video]');

      videos.forEach((video) => {
        if (e.matches) {
          video.pause();
        } else {
          video.play().catch(() => { });
        }
      });
    }


    /**
     * Setup modal functionality
     */
    setupModal() {
      if (!this.modal) return;

      const modalTextSliderEl = document.getElementById('product-features-modal-slider-' + this.extractedSectionId);
      const modalImageSliderEl = document.getElementById('product-features-modal-image-slider-' + this.extractedSectionId);

      if (!modalTextSliderEl || !modalImageSliderEl) return;

      this.modalTextSliderEl = modalTextSliderEl;
      this.modalImageSliderEl = modalImageSliderEl;

      // Event listeners for opening modal
      const openButtons = this.querySelectorAll('.js-open-feature-modal');
      openButtons.forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const index = parseInt(button.getAttribute('data-feature-index'));
          this.openModal(index);
        });
      });

      // Event listeners for closing modal
      const closeButtons = this.modal.querySelectorAll('[data-modal-close]');
      closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });

      // Close on ESC key
      document.addEventListener('keydown', this.handleKeydown);
    }

    /**
     * Initialize modal sliders
     */
    initModalSliders(startIndex) {
      if (!this.modalTextSliderEl || !this.modalImageSliderEl) return;

      // Destroy existing instances if they exist
      if (this.modalTextSlider) {
        try {
          this.modalTextSlider.destroy();
        } catch (e) {
          console.warn('Error destroying text slider:', e);
        }
        this.modalTextSlider = null;
      }
      if (this.modalImageSlider) {
        try {
          this.modalImageSlider.destroy();
        } catch (e) {
          console.warn('Error destroying image slider:', e);
        }
        this.modalImageSlider = null;
      }

      // Text slider
      this.modalTextSlider = new Splide(this.modalTextSliderEl, {
        type: 'fade',
        rewind: true,
        arrows: false,
        pagination: false,
        drag: false,
        start: startIndex,
        speed: 0,
      });

      // Image slider (synced with text) - with peek effect
      this.modalImageSlider = new Splide(this.modalImageSliderEl, {
        type: 'loop',
        rewind: true,
        loop: true,
        arrows: false,
        pagination: false,
        perPage: 1,
        perMove: 1,
        gap: '1rem',
        padding: { right: '20%', left: 0 },
        focus: 'left',
        trimSpace: false,
        drag: true,
        start: startIndex,
        speed: 0,
        breakpoints: {
          1099: {
            padding: { right: '8%', left: 0 },
          },
          767: {
            padding: { right: '25%', left: '25%' },
            focus: 'center',
          },
          576: {
            padding: { right: '15%', left: '15%' },
            focus: 'center',
          },
          479: {
            padding: { right: '10%', left: '10%' },
            focus: 'center',
          }
        },
      });

      // Setup after mount - similar to product gallery approach
      this.modalTextSlider.on('mounted', () => {
        // Refresh to recalculate positions, then restore speed
        requestAnimationFrame(() => {
          this.modalTextSlider.refresh();
          this.modalImageSlider.refresh();

          // Force go to the correct index with speed 0
          requestAnimationFrame(() => {
            this.modalTextSlider.go(startIndex);
            this.modalImageSlider.go(startIndex);

            // Restore animation speed after positioning is correct
            requestAnimationFrame(() => {
              this.modalTextSlider.options.speed = 600;
              this.modalImageSlider.options.speed = 600;
            });
          });
        });
      });

      // Sync sliders - image slider is the controller
      this.modalImageSlider.sync(this.modalTextSlider);

      this.modalTextSlider.mount();
      this.modalImageSlider.mount();

      // Setup modal slider controls using utility
      window.SplideUtils.setupSliderControls(this.modalImageSlider, this.modal, {
        customArrows: true,
        arrowSelectors: {
          prev: '.js-modal-arrow-prev',
          next: '.js-modal-arrow-next'
        },
        scrollbar: true
      });

      // Handle reduced motion for modal videos
      this.initModalVideoControls();
    }

    /**
     * Initialize modal video controls
     */
    initModalVideoControls() {
      const modalVideos = this.modal.querySelectorAll('[data-decorative-video]');

      modalVideos.forEach((video) => {
        // Pause video if reduced motion is preferred
        if (this.prefersReducedMotion.matches) {
          video.pause();
        }

        // Click on video to toggle play/pause (only effective when reduced motion is on)
        if (!video._hasClickHandler) {
          video._hasClickHandler = true;
          video.addEventListener('click', () => {
            if (!this.prefersReducedMotion.matches) return;

            if (video.paused) {
              video.play();
            } else {
              video.pause();
            }
          });
        }
      });
    }


    /**
     * Focus trap handler
     */
    trapFocus(e) {
      if (e.key !== 'Tab') return;

      const focusableElements = Array.from(this.modal.querySelectorAll(this.focusableElements))
        .filter(el => el.offsetParent !== null && !el.disabled);

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      // If shift+tab on first element, focus last
      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
      // If tab on last element, focus first
      else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }

    /**
     * Set inert on all siblings of modal to prevent focus escape
     */
    setPageInert(isInert) {
      let current = this.modal;

      // Traverse from modal up to body, making siblings inert at each level
      while (current && current.parentElement) {
        const parent = current.parentElement;
        const siblings = parent.children;

        for (let i = 0; i < siblings.length; i++) {
          const sibling = siblings[i];
          // Skip the current element in the path to modal, and script/style tags
          if (sibling === current || sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') continue;

          if (isInert) {
            sibling.setAttribute('inert', '');
            sibling.setAttribute('aria-hidden', 'true');
          } else {
            sibling.removeAttribute('inert');
            sibling.removeAttribute('aria-hidden');
          }
        }

        // Stop at body
        if (parent === document.body) break;
        current = parent;
      }
    }

    /**
     * Open modal
     */
    openModal(index) {
      // Ensure Splide is available before opening modal
      if (typeof Splide === 'undefined') {
        console.warn('Splide not loaded yet, cannot open modal');
        return;
      }

      if (this.stickyPdpForm) {
        this.stickyPdpForm.classList.remove('is-visible');
      }

      // Store currently focused element
      this.lastFocusedElement = document.activeElement;

      // Show modal
      this.modal.classList.add('is-active');
      document.body.classList.add('modal-open');
      this.modal.setAttribute('aria-hidden', 'false');

      // Make rest of page inert to trap focus
      this.setPageInert(true);

      // Initialize sliders after modal is visible
      requestAnimationFrame(() => {
        this.initModalSliders(index);
      });

      // Add focus trap listener
      document.addEventListener('keydown', this.trapFocusHandler);

      // Focus close button
      const closeButton = this.modal.querySelector('.product-features-modal__close');
      if (closeButton) {
        setTimeout(() => {
          closeButton.focus();
        }, 100);
      }
    }

    /**
     * Close modal
     */
    closeModal() {
      if (this.stickyPdpForm) {
        this.stickyPdpForm.classList.add('is-visible');
      }

      this.modal.classList.remove('is-active');
      document.body.classList.remove('modal-open');
      this.modal.setAttribute('aria-hidden', 'true');

      // Remove inert from rest of page
      this.setPageInert(false);

      // Remove focus trap listener
      document.removeEventListener('keydown', this.trapFocusHandler);

      // Restore focus to element that opened modal
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
        this.lastFocusedElement = null;
      }
    }

    /**
     * Handle ESC key to close modal
     */
    handleEscapeKey(e) {
      if (e.key === 'Escape' && this.modal && this.modal.classList.contains('is-active')) {
        this.closeModal();
      }
    }

    /**
     * Debounce utility
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    /**
     * Cleanup on disconnect
     */
    disconnectedCallback() {
      // Remove event listeners
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('keydown', this.trapFocusHandler);
      this.prefersReducedMotion.removeEventListener('change', this.handleMotionChange);

      // Destroy Splide instances
      if (this.mainSplide) {
        try {
          this.mainSplide.destroy();
        } catch (e) {
          console.warn('Error destroying main slider:', e);
        }
      }
      if (this.modalTextSlider) {
        try {
          this.modalTextSlider.destroy();
        } catch (e) {
          console.warn('Error destroying modal text slider:', e);
        }
      }
      if (this.modalImageSlider) {
        try {
          this.modalImageSlider.destroy();
        } catch (e) {
          console.warn('Error destroying modal image slider:', e);
        }
      }
    }
  }

  // Define custom element
  if (customElements.get('product-features-section') === undefined) {
    customElements.define('product-features-section', ProductFeaturesSection);
  }
})();

